import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { getShowcaseProjects } from '@data/showcaseProjects';
import { useI18n } from '../../i18n';
import { trackActivityEvent, usePageActivityTracker } from '@services/activityTracker';

export default function Gallery() {
	const { user } = useAuth();
	usePageActivityTracker('gallery');
	const { language, t } = useI18n();
	const [searchParams, setSearchParams] = useSearchParams();
	const projectId = searchParams.get('project');
	const mockProjects = getShowcaseProjects(language);
	
	const [selectedProject, setSelectedProject] = useState<string | null>(projectId || null);

	const handleCloseModal = () => {
		setSelectedProject(null);
		setSearchParams({});
	};

	// Sync selectedProject with URL parameter
	useEffect(() => {
		const projectParam = searchParams.get('project');
		setSelectedProject(projectParam || null);
	}, [searchParams]);

	useEffect(() => {
		if (!selectedProject) return;
		void trackActivityEvent({
			event_name: 'gallery_project_open',
			properties: { project_id: selectedProject }
		});
	}, [selectedProject]);

	// Handle ESC key to close modal and body scroll lock
	useEffect(() => {
		if (selectedProject) {
			// Prevent body scroll when modal is open
			document.body.style.overflow = 'hidden';
			
			// Handle ESC key
			const handleEsc = (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					setSelectedProject(null);
					setSearchParams({});
				}
			};
			document.addEventListener('keydown', handleEsc);
			
			return () => {
				document.body.style.overflow = '';
				document.removeEventListener('keydown', handleEsc);
			};
		}
	}, [selectedProject, setSearchParams]);

	// Find selected project
	const project = selectedProject ? mockProjects.find(p => p.id === selectedProject) : null;

	const ctaHref = user ? '/app/dashboard' : '/login';
	const ctaLabel = user ? t('gallery.cta.primary.user', 'Get Started') : t('gallery.cta.primary.guest', 'Sign Up Now');
	const localeByLanguage = { en: 'en-US', de: 'de-DE', hr: 'hr-HR' } as const;

	return (
		<>
			<section className="landing-hero py-5">
				<div className="container">
					<div className="text-center mb-5">
						<h1 className="display-4 fw-bold text-success-emphasis mb-3">
							{t('gallery.hero.title', 'Project Gallery')}
						</h1>
						<p className="lead text-muted">
							{t(
								'gallery.hero.subtitle',
								'Explore our completed garden transformations. Each project tells a unique story of vision, challenges, and beautiful results.'
							)}
						</p>
					</div>
				</div>
			</section>

			<section className="landing-section py-5 bg-white">
				<div className="container">
					<div className="text-center mb-5" style={{ opacity: 1, visibility: 'visible' }}>
						<h2 className="display-5 fw-bold text-success-emphasis mb-3" style={{ opacity: 1, visibility: 'visible' }}>
							{t('gallery.featured.title', 'Featured Projects')}
						</h2>
						<p className="text-muted lead fs-5">
							{t(
								'gallery.featured.subtitle',
								'Browse through our collection of completed garden transformations. Click on any project to see the full story.'
							)}
						</p>
					</div>
					<div className="row g-4">
						{mockProjects.map((projectItem) => (
							<div key={projectItem.id} className="col-12 col-md-6 col-lg-4">
								<div
									className="transform-card h-100 cursor-pointer"
									onClick={() => {
										setSelectedProject(projectItem.id);
										setSearchParams({ project: projectItem.id });
									}}
									style={{ cursor: 'pointer' }}
								>
									<div className="transform-images">
										<div>
											<span className="label">{t('gallery.label.before', 'Before')}</span>
											<img
												src={projectItem.before}
												alt={`${projectItem.title} before`}
												onError={(e) => {
													console.error('Failed to load image:', projectItem.before);
												}}
											/>
										</div>
										<div>
											<span className="label">{t('gallery.label.after', 'After')}</span>
											<img
												src={projectItem.after}
												alt={`${projectItem.title} after`}
												onError={(e) => {
													console.error('Failed to load image:', projectItem.after);
												}}
											/>
										</div>
									</div>
									<div className="transform-body">
										<div className="badge rounded-pill text-bg-success mb-3">{projectItem.style}</div>
										<h3 className="h5 fw-bold text-success-emphasis mb-2">{projectItem.title}</h3>
										<p className="text-muted small mb-2">{projectItem.description}</p>
												<div className="d-flex align-items-center gap-3 small text-muted">
													<span><i className="bi bi-geo-alt me-1"></i>{projectItem.location}</span>
													<span><i className="bi bi-calendar me-1"></i>{projectItem.details.duration}</span>
												</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className="landing-section py-5 bg-light">
				<div className="container">
					<div className="cta-tile px-4 py-5 shadow-lg position-relative overflow-hidden">
						<div className="row align-items-center g-4">
							<div className="col-lg-7">
								<h2 className="display-6 fw-bold text-success-emphasis mb-3">
									{t('gallery.cta.title', 'Ready to start your garden transformation?')}
								</h2>
								<p className="text-muted mb-0">
									{t(
										'gallery.cta.subtitle',
										'Join our community of garden seekers and expert gardeners. Create your plan, get quotes, and bring your vision to life.'
									)}
								</p>
							</div>
							<div className="col-lg-5 d-flex flex-column flex-sm-row gap-3 justify-content-end">
								<Link to={ctaHref} className="btn btn-success btn-lg">
									{ctaLabel}
								</Link>
								<Link to="/pricing" className="btn btn-outline-success btn-lg">
									{t('gallery.cta.secondary', 'View Pricing')}
								</Link>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Project Detail Modal */}
			{project && (
				<>
					<div 
						className="modal fade show" 
						style={{ display: 'block', zIndex: 30000 }} 
						tabIndex={-1}
						onClick={(e) => {
							// Close modal when clicking backdrop
							if (e.target === e.currentTarget) {
								handleCloseModal();
							}
						}}
					>
						<div 
							className="modal-dialog modal-xl modal-dialog-scrollable"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="modal-content" onClick={(e) => e.stopPropagation()}>
							<div className="modal-header">
								<h2 className="modal-title fw-bold text-success-emphasis">{project.title}</h2>
								<button
									type="button"
									className="btn-close"
									onClick={handleCloseModal}
									aria-label={t('gallery.modal.close', 'Close')}
								></button>
							</div>
							<div className="modal-body">
								<div className="mb-4">
									<p className="lead text-muted mb-4">{project.description}</p>
									<div className="row g-4 mb-4">
										<div className="col-12 col-lg-6">
											<div className="position-relative" style={{ aspectRatio: '16/9', overflow: 'hidden', borderRadius: '1rem' }}>
												<span className="badge bg-dark position-absolute top-0 start-0 m-3" style={{ zIndex: 1 }}>{t('gallery.label.before', 'Before')}</span>
												<img
													src={project.before}
													alt={`${project.title} before`}
													className="w-100 h-100"
													style={{ objectFit: 'cover', borderRadius: '1rem' }}
													onError={(e) => {
														console.error('Failed to load image:', project.before);
													}}
												/>
											</div>
										</div>
										<div className="col-12 col-lg-6">
											<div className="position-relative" style={{ aspectRatio: '16/9', overflow: 'hidden', borderRadius: '1rem' }}>
												<span className="badge bg-success position-absolute top-0 start-0 m-3" style={{ zIndex: 1 }}>{t('gallery.label.after', 'After')}</span>
												<img
													src={project.after}
													alt={`${project.title} after`}
													className="w-100 h-100"
													style={{ objectFit: 'cover', borderRadius: '1rem' }}
													onError={(e) => {
														console.error('Failed to load image:', project.after);
													}}
												/>
											</div>
										</div>
									</div>
								</div>

								<div className="row g-4">
									<div className="col-12 col-lg-8">
										<div className="card border-0 shadow-sm mb-4">
											<div className="card-body p-4">
												<h3 className="h5 fw-bold text-success-emphasis mb-4">{t('gallery.modal.details', 'Project Details')}</h3>
												<div className="row g-3 mb-4">
													<div className="col-6 col-md-3">
														<div className="text-muted small">{t('gallery.modal.location', 'Location')}</div>
														<div className="fw-semibold">{project.location}</div>
													</div>
													<div className="col-6 col-md-3">
														<div className="text-muted small">{t('gallery.modal.area', 'Area')}</div>
														<div className="fw-semibold">{project.details.area}</div>
													</div>
													<div className="col-6 col-md-3">
														<div className="text-muted small">{t('gallery.modal.duration', 'Duration')}</div>
														<div className="fw-semibold">{project.details.duration}</div>
													</div>
													<div className="col-6 col-md-3">
														<div className="text-muted small">{t('gallery.modal.budget', 'Budget')}</div>
														<div className="fw-semibold">{project.details.budget}</div>
													</div>
												</div>
												<div className="mb-4">
													<h4 className="h6 fw-semibold mb-3">{t('gallery.modal.keyFeatures', 'Key Features')}</h4>
													<ul className="list-unstyled">
														{project.details.features.map((feature, idx) => (
															<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																<i className="bi bi-check-circle-fill text-success mt-1"></i>
																<span>{feature}</span>
															</li>
														))}
													</ul>
												</div>
												<div className="mb-4">
													<h4 className="h6 fw-semibold mb-3">{t('gallery.modal.plantSelection', 'Plant Selection')}</h4>
													<div className="row g-2">
														{project.details.plants.map((plant, idx) => (
															<div key={idx} className="col-12 col-md-6">
																<div className="d-flex align-items-center gap-2">
																	<i className="bi bi-flower1 text-success"></i>
																	<span className="small">{plant}</span>
																</div>
															</div>
														))}
													</div>
												</div>
											</div>
										</div>

										<div className="card border-0 shadow-sm">
											<div className="card-body p-4">
												<h3 className="h5 fw-bold text-success-emphasis mb-4">{t('gallery.modal.story', 'Project Story')}</h3>
												<div className="mb-4">
													<h4 className="h6 fw-semibold mb-3 text-warning">{t('gallery.modal.challenges', 'Challenges')}</h4>
													<ul className="list-unstyled">
														{project.details.challenges.map((challenge, idx) => (
															<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																<i className="bi bi-exclamation-triangle-fill text-warning mt-1"></i>
																<span>{challenge}</span>
															</li>
														))}
													</ul>
												</div>
												<div>
													<h4 className="h6 fw-semibold mb-3 text-success">{t('gallery.modal.solutions', 'Solutions')}</h4>
													<ul className="list-unstyled">
														{project.details.solutions.map((solution, idx) => (
															<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																<i className="bi bi-lightbulb-fill text-success mt-1"></i>
																<span>{solution}</span>
															</li>
														))}
													</ul>
												</div>
											</div>
										</div>
									</div>

									<div className="col-12 col-lg-4">
										<div className="card border-0 shadow-sm bg-success-subtle">
											<div className="card-body p-4">
												<h3 className="h5 fw-bold text-success-emphasis mb-3">{t('gallery.modal.testimonial', 'Client Testimonial')}</h3>
												<blockquote className="mb-0">
													<p className="text-muted mb-3">"{project.details.testimonial}"</p>
													<footer className="small text-muted">
														— {project.client}
													</footer>
												</blockquote>
											</div>
										</div>
										<div className="card border-0 shadow-sm mt-4">
											<div className="card-body p-4">
												<h3 className="h5 fw-bold text-success-emphasis mb-3">{t('gallery.modal.info', 'Project Info')}</h3>
												<div className="small">
													<div className="mb-2">
														<span className="text-muted">{t('gallery.modal.client', 'Client')}:</span>
														<div className="fw-semibold">{project.client}</div>
													</div>
													<div className="mb-2">
														<span className="text-muted">{t('gallery.modal.completed', 'Completed')}:</span>
														<div className="fw-semibold">{new Date(project.completedDate).toLocaleDateString(localeByLanguage[language], { year: 'numeric', month: 'long', day: 'numeric' })}</div>
													</div>
													<div className="mb-2">
														<span className="text-muted">{t('gallery.modal.style', 'Style')}:</span>
														<div className="fw-semibold">{project.style}</div>
													</div>
												</div>
											</div>
										</div>
										<Link to={ctaHref} className="btn btn-success w-100 mt-4" onClick={handleCloseModal}>
											{ctaLabel}
										</Link>
									</div>
								</div>
							</div>
							</div>
						</div>
					</div>
					<div 
						className="modal-backdrop fade show" 
						onClick={handleCloseModal}
						style={{ opacity: 0.25, zIndex: 29900 }}
					></div>
				</>
			)}
		</>
	);
}

